import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { useQuery, useMutation } from '@apollo/client';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { v4 } from 'uuid';

import { ErrorHandler } from '@playbookux/playbook-web-core';
import TemplateQuestions from '../../../components/template-questions';

import {
  GET_TEMPLATE_WITH_SURVEY_BY_TEMPLATE_ID,
  UPDATE_TEMPLATE,
} from '../../../../core/graphql/templates';

import {
  setTemplateValues,
} from '../../../../core/actions';

import {
  Container,
  ModalSave,
  ModalCancel,
} from './styles';

const EditTemplatePage = (props) => {
  const {
    history,
    match: { params = {} },
    onSetTemplateValues,
  } = props;

  const [showModalCancel, setShowModalCancel] = useState(false);
  const [showModalSave, setShowModalSave] = useState(false);
  const [valuesSave, setValuesSave] = useState(null);

  const { templateId } = params;

  const [
    updateTemplate,
    { loading: loadingUpdate, error: errorUpdate },
  ] = useMutation(UPDATE_TEMPLATE);

  const {
    data = {},
    error,
    loading,
  } = useQuery(GET_TEMPLATE_WITH_SURVEY_BY_TEMPLATE_ID, {
    variables: {
      templateId,
    },
  });

  const { template = {} } = data;

  const templateValues = useMemo(() => {
    if (!_.isEmpty(template.survey)) {
      const taskSurvey = _.omit(template.survey, ['__typename']);

      const tasksQuestions = _.chain(taskSurvey.questions)
        .map((task) => {
          const taskValue = _.omit(task, ['__typename']);

          const optionQuestions = _.map(task.questionOptions, (questionOption) => (
            _.omit(questionOption, ['__typename'])));

          return ({
            ...taskValue,
            range: optionQuestions.length,
            questionOptions: optionQuestions,
            ...(task.questionType === 'range' && {
              leftLabel: task.leftLabel,
              rightLabel: task.rightLabel,
            }),
          });
        })
        .sortBy('questionOrder')
        .value();

      return {
        tasks: tasksQuestions,
        templateDescription: template.description,
        context: template.templateContext,
        templateName: template.name,
        surveyId: taskSurvey.id,
      };
    }

    return [];
  }, [template]);

  const handleClose = () => {
    history.push('/template-library');
  };

  const handleShowModalCancel = () => {
    setShowModalCancel((prev) => !prev);
  };

  /**
   * @description Sanitizes valuesSave, removing question id and question_options id
   * if isNew is true
   * @returns {object}
   */
  const sanitizeValues = () => {
    if (valuesSave) {
      const sanitizedValues = _.cloneDeep(valuesSave);
      let qAttrs = _.get(sanitizedValues, 'surveyAttributes.questionsAttributes');
      qAttrs = qAttrs.map((questionsAttributes) => {
        const updatedQAttrs = _.cloneDeep(questionsAttributes);
        delete updatedQAttrs.imageUrl;
        delete updatedQAttrs.videoUrl;

        if (updatedQAttrs.isNew) {
          let qOAttrs = _.get(updatedQAttrs, 'questionOptionsAttributes');
          qOAttrs = qOAttrs.map((questionOptionsAttributes) => {
            const updatedQOAttrs = _.cloneDeep(questionOptionsAttributes);
            delete updatedQOAttrs.id;
            return updatedQOAttrs;
          });
          updatedQAttrs.questionOptionsAttributes = qOAttrs;

          delete updatedQAttrs.isNew;
          delete updatedQAttrs.id;
        }

        return updatedQAttrs;
      });

      sanitizedValues.surveyAttributes.questionsAttributes = qAttrs;

      return sanitizedValues;
    }

    return null;
  };

  const handleSubmit = async () => {
    if (valuesSave) {
      const sanitizedValues = sanitizeValues();
      try {
        await updateTemplate({
          variables: {
            id: templateId,
            attributes: sanitizedValues,
          },
        });

        handleClose();
      } catch (ex) {
        // console.log('put in the pocket: ',ex.message);
      }
    }
  };

  const handleShowModalSave = (values) => {
    if (values) {
      setValuesSave(values);
    }
    setShowModalSave((prev) => !prev);
  };

  const handlePreviewStudy = (values) => {
    if (values) {
      const tasks = _.map(values.tasks, (task) => {
        const questionOptions = [];
        if (task.questionType === 'range') {
          for (let index = 1; index <= task.range; index += 1) {
            questionOptions.push({
              id: v4(), label: `${index}`, disqualify: null, qualify: false,
            });
          }
        } else if (task.questionOptions) {
          _.forEach(task.questionOptions, (questionOption) => {
            questionOptions.push({
              id: v4(), label: questionOption.label, disqualify: null, qualify: false,
            });
          });
        }

        return { ...task, questionOptions, id: task.id };
      });

      const previewValue = {
        ...values,
        tasks,
      };

      onSetTemplateValues(previewValue);
      window.open('/templates/preview-study');
    }
  };

  if (error) {
    return (
      <Container center>
        <ErrorHandler
          error={error}
          defaultMessage="Error while retrieving templates."
        />
      </Container>
    );
  }

  return (
    <>
      <Container>

        <TemplateQuestions
          title="Edit Template Questions"
          onCancel={handleShowModalCancel}
          onSave={handleShowModalSave}
          templateValues={templateValues}
          loading={loading}
          onPreviewStudy={handlePreviewStudy}
          isEdit
        />

      </Container>

      {showModalCancel && (
        <ModalCancel
          show={showModalCancel}
          onCancel={handleShowModalCancel}
          onConfirm={handleClose}
          isLoading={loading}
          textCancel="No, don't cancel"
          textConfirm="Yes, cancel"
          textContent="Do you want to cancel? Your updates will not be saved."
        />
      )}

      {showModalSave && (
        <ModalSave
          show={showModalSave}
          error={errorUpdate}
          onCancel={handleShowModalSave}
          onConfirm={handleSubmit}
          isLoading={loadingUpdate}
          textCancel="Cancel"
          textConfirm="Save"
          textContent="Do you want to save your updates?"
        />
      )}

    </>
  );
};

EditTemplatePage.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      templateId: PropTypes.string,
    }),
  }).isRequired,
  onSetTemplateValues: PropTypes.func.isRequired,
};

const mapDispatchToProps = {
  onSetTemplateValues: setTemplateValues,
};

const enhance = compose(
  connect(null, mapDispatchToProps),
);

export default enhance(EditTemplatePage);
